import { Text } from '@volkswagen-onehub/components-core';
import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { dpsSend } from 'src/bff';
import { CarlineSelect, DetalleMotivo, MultipleCTASelection } from 'src/components';
import { PeriodoContacto } from 'src/components/PeriodoContacto';
import { FormController, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { FormDataGroup, FormInfo, IdLead, MolecularResponse, OneFormState, Steps } from 'src/types';
import { formDataBuilder } from '../format-fields';
import { useInitializeLEMForm } from './';
import { LEMLastScreenFields, ThanKYouLEM } from 'src/forms/LEM';
import { LEMFormProps } from './LEMController';
import { checkIdLeadHasCarline } from './use-initialize-lem-form';
import { CTAsSiNo, CTAsTipoVisita, CTAsTipoVisitaNo } from 'src/feature-app/InputsValues/CTAsObjects';

export enum LEMContactadoSteps {
  Modelo,
  PeriodoContacto,
  ProximoContacto,
  TipoVisita,
  MasInformacion,
  EnviarInformacion,
}

const getTipoVisitaTitle = (contactIsYes: boolean) => {
  if (contactIsYes) {
    return (
      <>
        <Text bold>¿Cómo será </Text>este próximo{' '}
        <span style={{ whiteSpace: 'nowrap' }}>
          contacto?
          <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </span>
      </>
    );
  } else {
    return (
      <>
        <Text bold>¿Por qué no</Text>{' '}
        <span style={{ whiteSpace: 'nowrap' }}>
          exactamente?
          <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </span>
      </>
    );
  }
};

const getTipoVisitaFields = (contactIsYes: boolean) => {
  if (contactIsYes) {
    return (
      <OneElementTemplateFullScreen
        element={<MultipleCTASelection ctaValues={CTAsTipoVisita} name="tipoVisita" nextOnClick />}
      />
    );
  } else {
    return (
      <OneElementTemplateFullScreen
        element={<MultipleCTASelection ctaValues={CTAsTipoVisitaNo} name="tipoVisita" nextOnClick />}
      />
    );
  }
};

const getPeriodoContactoTitle = (modeloPrecargado: boolean) => {
  if (modeloPrecargado) {
    return (
      <>
        <Text bold>Y ¿cuándo</Text> te{' '}
        <span style={{ whiteSpace: 'nowrap' }}>
          respondieron?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </span>
      </>
    );
  } else {
    return (
      <>
        Nos encantaría saber más sobre tu experiencia.{' '}
        <Text bold>
          ¿Cuándo te{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            respondieron?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </Text>
      </>
    );
  }
};

export function LEMContactadoForm(props: LEMFormProps) {
  const { dpsData, idLead, idPerson, handleError, presubmit, trackFormLoad, showPantallaModelo } = props;
  const [loadingPresubmit, setLoadingPresubmit] = useState(true);
  const { initializeForm } = useOneFormContext();
  const store = useStore();
  const { proximoContacto } = useSelector((state: OneFormState) => state.formData.fields);
  const dispatch = useDispatch();
  const stepsWithModel: Steps[] = [
    {
      title: (
        <>
          <Text bold>¿Por qué modelo </Text>contactaste con tu concesionario?
        </>
      ),
      fields: <OneElementTemplateFullScreen element={<CarlineSelect useCarline={true} notALayer={true} />} />,
      screenIndex: LEMContactadoSteps.Modelo,
      name: 'Modelo',
      outputs: ['modelo'],
      outputsText: ['modelo'],
    },
    {
      title: getPeriodoContactoTitle(showPantallaModelo),
      fields: <OneElementTemplateFullScreen element={<PeriodoContacto />} />,
      screenIndex: LEMContactadoSteps.PeriodoContacto,
      name: 'PeriodoContacto',
      outputs: ['periodoContacto'],
      outputsText: ['periodoContacto'],
    },
    {
      title: (
        <>
          ¿Habéis acordado un{' '}
          <Text bold>
            próximo{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              contacto?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={<MultipleCTASelection ctaValues={CTAsSiNo} name="proximoContacto" ctaWidth="81" nextOnClick />}
        />
      ),
      screenIndex: LEMContactadoSteps.ProximoContacto,
      name: 'ProximoContacto',
      outputs: ['proximoContacto'],
      outputsText: ['proximoContacto'],
      trueOrFalseQuestion: true,
    },
    {
      title: getTipoVisitaTitle(true),
      fields: getTipoVisitaFields(true),
      screenIndex: LEMContactadoSteps.TipoVisita,
      name: 'TipoVisita',
      outputs: ['tipoVisita'],
      outputsText: ['tipoVisita'],
    },
    {
      title: (
        <>
          <Text bold>¿Hay algo más</Text> que nos quieras{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            contar?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </>
      ),
      fields: <OneElementTemplateFullScreen element={<DetalleMotivo />} />,
      screenIndex: LEMContactadoSteps.MasInformacion,
      name: 'MasInformacion',
      outputs: ['masInformacion'],
      outputsText: ['masInformacion'],
    },
    {
      title: (
        <>
          <Text bold>¿Podemos compartir tu respuesta</Text> con el{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            concesionario?
            <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
          </span>
        </>
      ),
      fields: <LEMLastScreenFields />,
      screenIndex: LEMContactadoSteps.EnviarInformacion,
      name: 'EnviarInformacion',
      outputs: ['enviarInformacion'],
      outputsText: ['enviarInformacion'],
    },
  ];

  const LEMContactadoData: Partial<FormDataGroup> = {
    headers: {},
    fields: {
      formName: 'lem-contactado',
      formType: 'otherform',
      pageCategory: 'Encuesta',
    }, // Analítica
  };

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        handleError();
      }
      setLoadingPresubmit(false);
    };
    if (presubmit) {
      sendPresubmit();
    } else {
      setLoadingPresubmit(false);
    }
  }, []);

  useEffect(() => {
    if (proximoContacto) {
      modifyStepsNextContact(proximoContacto);
    }
  }, [proximoContacto]);

  const modifyStepsNextContact = (proximoContacto: 'true' | 'false') => {
    const newSteps = [...steps];
    const indexOfVisita = newSteps.findIndex((step) => step.name === 'TipoVisita');

    newSteps[indexOfVisita].title = getTipoVisitaTitle(proximoContacto === 'true');
    newSteps[indexOfVisita].fields = getTipoVisitaFields(proximoContacto === 'true');

    dispatch({ type: 'UPDATE_STEPS', payload: newSteps });
  };

  const getCarlineFields = (fields: any, idLead: IdLead) => {
    if (checkIdLeadHasCarline(idLead)) {
      return {
        'fields[COD_INTEREST_CARLINE]': idLead.NUM_COD_INTEREST_CARLINE__c, // SOLO EN SUBMIT
        'fields[DESC_INTEREST_CARLINE]': idLead.DESC_Model__c, // SOLO EN SUBMIT
      };
    } else {
      return {
        'fields[COD_INTEREST_CARLINE]': fields.carlineCode, // SOLO EN SUBMIT
        'fields[DESC_INTEREST_CARLINE]': fields.modelo, // SOLO EN SUBMIT
      };
    }
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { tc, today, url, device } = getParamsForm();

    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_VN_VO]': 'VN',

      'headers[DESC_TOKEN]': 'VW_DDB_FRM_LEM_ENCUESTA_CONTESTADO', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_ID_CLIMAPRO]': idLead.ID_Person_DIM_IMP__c,
      'headers[DESC_ID_CAMPAIGN]': '7010Y0000001tAOQAY',
      'headers[DESC_FORM_DETAIL]': 'LEM Contactado', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORMOBJECTIVE]': 'Encuesta',
      'headers[DESC_DEVICETYPE]': device,
      'headers[DESC_CAMPAIGNBLOCK]': '9. VW LEM',
      'headers[DESC_BRAND]': 'VW',
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,

      'fields[NUM_CELLPHONE]': idPerson.NUM_Phone_mobile__c,
      'fields[M_OBSERVATION]': fields.masInformacion, // SOLO EN SUBMIT
      'fields[DESC_SURNAME_2]': idPerson.TXT_2_Surname__c,
      'fields[DESC_SURNAME_1]': idPerson.LastName,
      'fields[DESC_NAME]': idPerson.FirstName,
      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_LEM_DEALER_PERFORMANCE]': dpsData.rating, // SOLO EN CONTACTADO
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      'fields[DESC_EMAIL_ADDRESS]': idPerson.TXT_Email__c,
      'fields[DESC_DEALER_SHARE]': fields.enviarInformacion === 'Sí' ? 'True' : 'False', // SOLO EN SUBMIT
      'fields[DESC_DEALER_CODE]': idLead.TXT_DESC_DEALER_CODE__c,
      'fields[DESC_DEALER]': idLead.DESC_Dealer_Code__c,
      'fields[DESC_AUXFIELD2]': fields.proximoContacto === 'true' ? 'True' : 'False',
      'fields[DESC_AUXFIELD1]': fields.tipoVisita, // SOLO EN SUBMIT Y CONTACTADO
      'fields[COD_LEM_CONTACT_TIME]': fields.periodoContacto, // SOLO EN SUBMIT
      'fields[COD_LEAD_ID]': idLead.Id,
      ...getCarlineFields(fields, idLead),

      'flags[FLAG_ISDATA_CHANGED]': 'false', // Solo puede ser true en grabación No Contactado
      'flags[FLAG_CONTACTED]': dpsData.contactado,
    };
  };

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(LEMContactadoData);

    delete data['fields[COD_LEM_CONTACT_TIME]'];
    delete data['fields[DESC_AUXFIELD1]'];
    delete data['fields[DESC_AUXFIELD2]'];
    delete data['fields[M_OBSERVATION]'];
    delete data['fields[DESC_DEALER_SHARE]'];
    delete data['fields[DESC_NAME]'];
    delete data['fields[DESC_SURNAME_1]'];
    delete data['fields[DESC_SURNAME_2]'];
    delete data['fields[DESC_EMAIL_ADDRESS]'];
    delete data['fields[NUM_CELLPHONE]'];
    delete data['fields[COD_INTEREST_CARLINE]'];
    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_INTEREST_CARLINE]'];
    delete data['headers[PINCODE]'];

    data['headers[DESC_FORM_DETAIL]'] = 'LEM Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FRM_LEM_ENCUESTA';

    return data;
  };
  const generateSubmitData = () => generateData();

  const sendForm = (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();
      //resolve(true);
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);
      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response && response.data && response.data.content && response.data.content.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { loading, steps, firstStep, lastStep } = useInitializeLEMForm(stepsWithModel, idLead);

  useEffect(() => {
    const LEMContactadoInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThanKYouLEM />,
    };
    if (!loading) {
      initializeForm(LEMContactadoInfo, LEMContactadoData, true);
      trackFormLoad();
    }
  }, [loading]);

  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
